import {
    SCol,
    SIcon,
    SInputExtended,
    SPagination,
    SRow,
    STableContainer,
} from "@avalara/skylab-sdk/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchBucketLocations } from "../../../../app/bucketsSlice/bucketActions";
import {
    selectAvailableLocations,
    selectIsLoading,
    selectLinkedLocations,
} from "../../../../app/bucketsSlice/bucketSelectors";
import { selectSession, updateRowCountPreference } from "../../../../app/sessionSlice";
import featureFlag from "../../../../featureToggler/featureFlag";
import { useIsAuthorized } from "../../../../hooks/useIsAuthorized";
import { getPageTitle } from "../../../../shared/sessionUtility";
import commonRoutesConfig from "../../../navigation/commonRoutesConfig";
import { getGeneratedPathForRoute } from "../../../navigation/utils";
import NoData from "../../../search/NoData";
import RecordCount from "../../../search/RecordCount";
import Loader from "../../../sharedComponents/Loader";
import "../buckets.css";
import AssignLocation from "./AssignLocation";
import RemoveSelectedLocationsDialog from "./RemoveSelectedLocationsDialog";

export const countLabel = "locations";

const BucketLocationGrid = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { bucketId } = useParams();
    const session = useSelector(selectSession, shallowEqual);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedLocations, setSelectedLocations] = useState(new Set());
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(session.rowsPerPage || 20);
    const isLocationLoading = useSelector(selectIsLoading);
    const availableLocations = useSelector(selectAvailableLocations);
    const linkedLocations = useSelector(selectLinkedLocations);
    const canAddEditDeleteBucketsLocations = useIsAuthorized(
        featureFlag.settings.addEditDeleteBucketsLocations
    );

    const bucketName = location.state?.bucket?.name;
    const filteredAssignedLocations = linkedLocations.filter(location =>
        location.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalRecords = filteredAssignedLocations.length;

    // Ensure pagination boundaries are respected
    const { validatedRowsPerPage, validatedStartIndex, paginatedAssignedLocations } =
        useMemo(() => {
            const validatedRowsPerPage = Math.max(1, rowsPerPage);
            const validatedCurrentPage = Math.max(
                1,
                Math.min(currentPage, Math.ceil(totalRecords / validatedRowsPerPage) || 1)
            );
            const validatedStartIndex = (validatedCurrentPage - 1) * validatedRowsPerPage;

            const paginatedAssignedLocations =
                totalRecords > 0
                    ? filteredAssignedLocations.slice(
                          validatedStartIndex,
                          validatedStartIndex + validatedRowsPerPage
                      )
                    : [];

            return { validatedRowsPerPage, validatedStartIndex, paginatedAssignedLocations };
        }, [filteredAssignedLocations, totalRecords, currentPage, rowsPerPage]);

    useEffect(() => {
        dispatch(fetchBucketLocations(bucketId));
    }, [dispatch, bucketId]);

    useEffect(() => {
        return () => {
            setSelectedLocations(new Set());
            setSearchTerm("");
        };
    }, []);

    // Handle pagination validation when data changes
    useEffect(() => {
        if (filteredAssignedLocations.length > 0 && rowsPerPage > 0) {
            const maxPages = Math.ceil(filteredAssignedLocations.length / rowsPerPage);
            if (currentPage > maxPages) {
                setCurrentPage(maxPages);
                setSelectedLocations(new Set());
            }
        } else if (filteredAssignedLocations.length === 0 && currentPage > 1) {
            setCurrentPage(1);
            setSelectedLocations(new Set());
        }
    }, [filteredAssignedLocations.length, rowsPerPage, currentPage]);

    // Refetch data when locations are assigned/removed to ensure data consistency
    useEffect(() => {
        if (bucketId && linkedLocations.length > 0) {
            setSelectedLocations(new Set());

            const maxPages = Math.ceil(linkedLocations.length / rowsPerPage);
            if (currentPage > maxPages && maxPages > 0) {
                setCurrentPage(maxPages);
            }
        }
    }, [linkedLocations.length, bucketId, rowsPerPage, currentPage]);

    const handleLocationSelection = useCallback(
        e => {
            const locationId = parseInt(e.target.value, 10);
            const newSelection = new Set(selectedLocations);

            if (e.target.checked) {
                newSelection.add(locationId);
            } else {
                newSelection.delete(locationId);
            }

            setSelectedLocations(newSelection);
        },
        [selectedLocations]
    );

    const handleSelectAll = useCallback(
        e => {
            if (e.target.checked) {
                setSelectedLocations(new Set(paginatedAssignedLocations.map(loc => loc.id)));
            } else {
                setSelectedLocations(new Set());
            }
        },
        [paginatedAssignedLocations]
    );

    const handleRemoveSelectedLocations = useCallback(() => {
        if (selectedLocations.size > 0) {
            setIsRemoveDialogOpen(true);
        }
    }, [selectedLocations]);

    const handleRemoveLocation = useCallback(locationId => {
        setSelectedLocations(new Set([locationId]));
        setIsRemoveDialogOpen(true);
    }, []);

    const handleSearchInput = useCallback(e => {
        setSearchTerm(e.detail.value.trim());
        setCurrentPage(1);
        setSelectedLocations(new Set());
    }, []);

    const handleClearSearch = useCallback(() => {
        setSearchTerm("");
        setCurrentPage(1);
        setSelectedLocations(new Set());
    }, []);

    const handlePaginate = useCallback(
        e => {
            const { currentPage, rowsPerPage: newRowsPerPage, flag } = e.detail;

            if (flag && flag !== "") {
                if (newRowsPerPage !== session.rowsPerPage) {
                    setRowsPerPage(newRowsPerPage);
                    dispatch(updateRowCountPreference(newRowsPerPage));
                }
                setCurrentPage(currentPage);
                setSelectedLocations(new Set());
            }
        },
        [session.rowsPerPage, dispatch]
    );

    const renderActionButtons = row => (
        <button
            type="button"
            className="icon-leading text-blue-medium unstyled-button"
            onClick={() => handleRemoveLocation(row.id)}
            title="Remove location"
            disabled={!canAddEditDeleteBucketsLocations}>
            <SIcon name="trash" className="margin-right-xs" aria-label="Remove" />
        </button>
    );

    const handleBackToSettings = useCallback(() => {
        navigate(getGeneratedPathForRoute(commonRoutesConfig.settingsBuckets));
    }, [navigate]);

    const hasSearchTerm = searchTerm.trim().length > 0;
    const hasNoSearchResults = hasSearchTerm && filteredAssignedLocations.length === 0;

    const table = hasNoSearchResults ? (
        <NoData showGenNoDataMsg />
    ) : (
        <STableContainer tabIndex="0">
            <table>
                <thead>
                    <tr>
                        <th className="divider-end pad-all-sm checkbox-column-width">
                            <input
                                type="checkbox"
                                className="unlabeled"
                                onChange={handleSelectAll}
                                checked={
                                    paginatedAssignedLocations.length > 0 &&
                                    selectedLocations.size === paginatedAssignedLocations.length &&
                                    paginatedAssignedLocations.every(location =>
                                        selectedLocations.has(location.id)
                                    )
                                }
                                aria-label="Select all locations"
                            />
                        </th>
                        <th className="text-left text-align-center">Assigned Location</th>
                        <th className="text-right"></th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedAssignedLocations.map(row => (
                        <tr key={row.id}>
                            <td className="divider-end pad-all-sm checkbox-column-width">
                                <input
                                    type="checkbox"
                                    className="unlabeled"
                                    id={`location-${row.id}`}
                                    value={row.id}
                                    checked={selectedLocations.has(row.id)}
                                    onChange={handleLocationSelection}
                                    aria-label={`Select ${row.name}`}
                                />
                            </td>
                            <td className="text-left text-align-center">{row.name}</td>
                            <td className="text-right text-align-center">
                                {renderActionButtons(row)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </STableContainer>
    );

    return (
        <React.Fragment>
            <Helmet>
                <title>{dispatch(getPageTitle("Bucket Locations"))}</title>
            </Helmet>
            <h1 className="line-height-md">Manage locations in {bucketName || "Bucket"}</h1>

            <AssignLocation
                availableLocations={availableLocations}
                isLocationLoading={isLocationLoading}
                bucketId={bucketId}
            />

            {isLocationLoading ? (
                <Loader loading={isLocationLoading} />
            ) : !linkedLocations.length ? (
                <NoData bucket />
            ) : (
                <>
                    <h4>Search for assigned locations in the bucket</h4>
                    <SRow className="align-items-end margin-bottom-sm">
                        <SCol span="6">
                            <SInputExtended
                                inputId="locationSearch"
                                aria-label="Search by location name"
                                type="search"
                                placeholder="Enter a location name"
                                onS-input={handleSearchInput}
                                onS-clear={handleClearSearch}
                                value={searchTerm}
                            />
                        </SCol>
                        <SCol span="6" className="text-right">
                            <button
                                type="button"
                                className="secondary icon-leading"
                                onClick={handleRemoveSelectedLocations}
                                disabled={
                                    selectedLocations.size <= 1 || !canAddEditDeleteBucketsLocations
                                }>
                                Remove selected locations
                            </button>
                        </SCol>
                    </SRow>

                    <RecordCount
                        selectedCount={0}
                        recordCount={totalRecords || 0}
                        recordName={countLabel}
                    />

                    {table}

                    {totalRecords > 0 ? (
                        <SPagination
                            className="margin-top-md"
                            rowsPerPage={validatedRowsPerPage}
                            totalRecords={totalRecords}
                            startIndex={validatedStartIndex}
                            onS-paginate={handlePaginate}
                        />
                    ) : null}

                    {isRemoveDialogOpen ? (
                        <RemoveSelectedLocationsDialog
                            isRemoveDialogOpen={isRemoveDialogOpen}
                            setIsRemoveDialogOpen={setIsRemoveDialogOpen}
                            selectedLocations={selectedLocations}
                            setSelectedLocations={setSelectedLocations}
                            assignedLocations={linkedLocations}
                            bucketId={bucketId}
                        />
                    ) : null}
                </>
            )}

            <span className="flex justify-content-start">
                <button
                    className="secondary"
                    type="button"
                    data-testid="back-button"
                    onClick={handleBackToSettings}>
                    Back to bucket settings
                </button>
            </span>
        </React.Fragment>
    );
};

export default BucketLocationGrid;
